/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { Typography } from '@neo4j-ndl/react';
import classNames from 'classnames';
import { useId, useState } from 'react';

import { type CommonProps } from '../../_common/types';
import { ChevronDownIconOutline } from '../../icons/generated/heroIcons/ChevronDownIconOutline';
import { Presence } from '../presence';
import { formatThinkingDuration } from '../thinking/thinking-duration-formatter';

type ReasoningProps = {
  /** What should be displayed inside the reasoning component. */
  children?: React.ReactNode;
  /** Whether the AI is thinking */
  isThinking?: boolean;
  /** The duration of the thinking in milliseconds */
  thinkingMs?: number;
  /** The current action the AI is performing */
  currentAction?: string;
};

/**
 * The component is used to display the reasoning state of an LLM.
 * It has an accordion like behavior to show the reasoning steps.
 *
 * @alpha - Changes to this component may be breaking.
 */
const ReasoningComponent = ({
  isThinking = true,
  thinkingMs = 1000,
  currentAction = 'Thinking',
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', ReasoningProps>) => {
  const classes = classNames('ndl-ai-reasoning', className);
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldShowChildren = isExpanded && isThinking === false;
  const buttonId = useId();
  const panelId = useId();

  const { value: thinkingDuration, unit } = formatThinkingDuration(thinkingMs);

  return (
    <div
      ref={ref}
      className={classes}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      <Typography
        variant="label"
        className={classNames('ndl-ai-reasoning-header', {
          'ndl-expanded': isExpanded,
        })}
        {...(!isThinking
          ? {
              as: 'button',
              htmlAttributes: {
                'aria-controls': panelId,
                'aria-expanded': isExpanded,
                id: buttonId,
                onClick: () => setIsExpanded(!isExpanded),
                type: 'button',
              },
            }
          : { as: 'div', htmlAttributes: { id: buttonId } })}
      >
        {isThinking === true ? (
          <>
            <Presence
              isThinking={isThinking}
              className="ndl-ai-reasoning-presence"
            />
            <span className="ndl-ai-reasoning-header-text">
              {currentAction}...
            </span>
          </>
        ) : (
          <>
            <span>
              Thought for {thinkingDuration} {unit}
            </span>
            <ChevronDownIconOutline
              className={classNames('ndl-ai-reasoning-chevron', {
                'ndl-expanded': isExpanded,
              })}
            />
          </>
        )}
      </Typography>
      <div
        className={classNames('ndl-ai-reasoning-content', {
          'ndl-expanded': shouldShowChildren,
        })}
        inert={!shouldShowChildren}
        aria-hidden={!shouldShowChildren}
        aria-labelledby={buttonId}
        role="region"
        id={panelId}
      >
        <div className="ndl-ai-reasoning-content-inner">
          <div className="ndl-ai-reasoning-content-inner-2">{children}</div>
        </div>
      </div>
    </div>
  );
};

type SectionProps = {
  /** The heading of the section */
  heading?: React.ReactNode;
  /** The children of the section */
  children?: React.ReactNode;
  /** The visual leading the section */
  leadingVisual?: React.ReactNode;
  // TODO v5: remove this prop
  /** Whether the section is expanded.
   * @deprecated - Does nothing, will be removed in v5 */
  isExpanded?: boolean;
  // TODO v5: remove this prop
  /** The default value of the section is expanded.
   * @deprecated - Does nothing, will be removed in v5 */
  isDefaultExpanded?: boolean;
  // TODO v5: remove this prop
  /** Callback function triggered when the section is expanded.
   * @deprecated - Does nothing, will be removed in v5 */
  onExpandChange?: (isExpanded: boolean) => void;
};

const Section = ({
  heading,
  leadingVisual,
  children,
  className,
  style,
  htmlAttributes,
  ref,
  isExpanded: _isExpanded,
  isDefaultExpanded: _isDefaultExpanded,
  onExpandChange: _onExpandChange,
  ...restProps
}: CommonProps<'div', SectionProps>) => {
  const classes = classNames('ndl-ai-reasoning-section', className);

  return (
    <div
      ref={ref}
      className={classes}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      <div className="ndl-ai-reasoning-section-header">
        <div className="ndl-ai-reasoning-section-leading">{leadingVisual}</div>
        <Typography variant="subheading-small">{heading}</Typography>
      </div>
      <div className="ndl-ai-reasoning-section-content">
        <div className="ndl-ai-reasoning-section-content-line-container">
          <div className="ndl-ai-reasoning-section-content-line" />
        </div>
        <div className="ndl-ai-reasoning-section-content-children">
          {children}
        </div>
      </div>
    </div>
  );
};

const Footer = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', { children: React.ReactNode }>) => {
  const classes = classNames('ndl-ai-reasoning-footer', className);
  return (
    <Typography
      as="div"
      variant="body-small"
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </Typography>
  );
};

const Reasoning = Object.assign(ReasoningComponent, {
  Footer,
  Section,
});

export { Reasoning };
